import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Printer, Check, Lock, Sparkles, FileText } from "lucide-react";
import api from "@/lib/api";

export default function WeeklySummary() {
  const { week } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/weekly-summary/${week}`).then((r) => setData(r.data)).catch(() => {});
  }, [week]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="weekly-summary-loading">
        <div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  const loggedDays = data.days.filter((d) => d.entries.length > 0 || d.reflect_answer);

  return (
    <div className="px-5 pt-6 pb-16 animate-fade-rise print-area" data-testid="weekly-summary-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="weekly-back">
        <ChevronLeft size={20} /> Back
      </button>

      <p className="overline-label">Week {data.week} summary · Days {data.start} to {data.end}</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">{data.title}</h1>
      <p className="text-ink-soft text-sm mt-2">
        Everything you noted this week, gathered in one place. {data.completed_in_week} of {data.total_days_in_week} days complete.
      </p>

      {/* progress chip */}
      <div className="mt-4 bg-white rounded-2xl p-4 card-shadow flex items-center justify-between">
        <span className="text-ink-soft text-sm font-medium">This week</span>
        <span className="text-brand font-bold text-sm" data-testid="weekly-progress">
          {data.completed_in_week}/{data.total_days_in_week} days
        </span>
      </div>

      {loggedDays.length === 0 ? (
        <div className="mt-6 bg-app-secondary border border-subtle rounded-2xl p-6 text-center" data-testid="weekly-empty">
          <p className="text-ink-soft text-sm">
            {data.started
              ? "Nothing saved for this week yet. What you enter on each day will appear here."
              : "This week hasn't started yet. Complete the days before it to unlock this summary."}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {loggedDays.map((d) => (
            <div key={d.day} className="bg-white rounded-2xl p-5 card-shadow" data-testid={`weekly-day-${d.day}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${d.completed ? "bg-brand text-white" : "bg-[var(--surface-accent)] text-ink-muted"}`}>
                  {d.completed ? <Check size={16} strokeWidth={3} /> : <span className="text-xs font-bold">{d.day}</span>}
                </div>
                <div>
                  <div className="text-ink-muted text-[11px] font-semibold">DAY {d.day}</div>
                  <div className="font-semibold text-ink text-sm">{d.title}</div>
                </div>
              </div>

              {d.entries.length > 0 && (
                <div className="space-y-2">
                  {d.entries.map((e, i) => (
                    <div key={i} className="bg-app rounded-xl px-4 py-3">
                      <div className="text-ink-muted text-xs font-medium">{e.label}</div>
                      <div className="text-ink text-sm mt-0.5 whitespace-pre-wrap">{e.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {d.reflect_answer && (
                <div className="mt-2 bg-sage rounded-xl px-4 py-3">
                  <div className="text-brand text-xs font-semibold">Your reflection</div>
                  <div className="text-ink text-sm mt-0.5 whitespace-pre-wrap">{d.reflect_answer}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* final report note */}
      <div className="mt-6 bg-brand rounded-2xl p-5 text-white" data-testid="weekly-report-note">
        <div className="flex items-center gap-2 mb-1.5">
          {data.program_complete ? <FileText size={16} style={{ color: "var(--amber)" }} /> : <Lock size={16} style={{ color: "var(--amber)" }} />}
          <span className="text-sm font-semibold">Your Personal Menopause Support Plan</span>
        </div>
        {data.program_complete ? (
          <>
            <p className="text-white/75 text-sm leading-relaxed">
              You've finished all 30 days, so your full report is ready.
            </p>
            <button
              data-testid="weekly-view-plan-btn"
              onClick={() => navigate("/plan")}
              className="mt-4 w-full bg-white text-brand font-semibold py-3 rounded-full flex items-center justify-center gap-2"
            >
              <Sparkles size={16} /> View my plan
            </button>
          </>
        ) : (
          <p className="text-white/75 text-sm leading-relaxed">
            Everything you enter is being saved along the way. Your complete, personalised report is produced once you finish all 30 days.
          </p>
        )}
      </div>

      {loggedDays.length > 0 && (
        <button
          data-testid="weekly-print-btn"
          onClick={() => window.print()}
          className="mt-4 w-full border-2 border-[var(--brand)] text-brand font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 no-print"
        >
          <Printer size={16} /> Download / Print this week
        </button>
      )}
    </div>
  );
}
